import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNewsletterSubscribe } from 'src/hooks';
import { AlertPing, SimpleSpinner } from 'src/components';

type Props = {
  focusOnMount?: boolean;
  forFooter?: boolean; // defaults to true. False for ReceiveUnsupportedModal
};

export const NewsletterSubscribeForm = ({
  focusOnMount = false,
  forFooter = true,
}: Props) => {
  const [email, setEmail] = useState<string>('');

  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const newsletterSubscribe = useNewsletterSubscribe({ setEmail, inputRef });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    newsletterSubscribe.mutate(email);
  };

  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [focusOnMount]);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={`mx-auto my-2 flex w-full flex-col items-center justify-start gap-x-2.5 gap-y-2 ${forFooter ? 'max-w-[420px] md:mx-0 md:flex-row lg:max-w-full' : 'sm:mx-0 sm:flex-row lg:justify-center'}`}
      >
        <div
          className={`group relative h-10 w-full min-w-[200px] lg:w-[260px] lg:grow xl:w-[300px]`}
        >
          <input
            type='text'
            placeholder={t('footer.your_email')}
            value={email}
            ref={inputRef}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => {
              if (newsletterSubscribe.isError) {
                newsletterSubscribe.reset();
              }
            }}
            disabled={newsletterSubscribe.isPending}
            className={`h-10 w-full min-w-[200px] rounded border-none px-3 py-2 font-secondary font-semibold leading-[24px] text-primary-dark outline-none ring-2 transition-all selection:bg-primary selection:text-cloudGray placeholder:font-normal placeholder:text-darkGray focus:ring-2 disabled:bg-gray-300 disabled:opacity-60 disabled:ring-primary ${forFooter ? 'lg:w-[260px] xl:w-[300px]' : 'lg:grow'} ${
              newsletterSubscribe.isError
                ? 'bg-rose-100 ring-rose-500 hover:bg-rose-50 focus:hover:bg-rose-100'
                : newsletterSubscribe.isPending
                  ? 'focus:ring-primary'
                  : `${forFooter ? 'bg-cloudGray hover:bg-gray-200 focus:hover:bg-cloudGray' : 'bg-offWhite'} ring-transparent focus:ring-primary`
            }`}
          />

          {newsletterSubscribe.isError ? (
            <AlertPing variant='error' customX='-right-2' customY='-top-2' />
          ) : newsletterSubscribe.isPending ? (
            <SimpleSpinner customX='-right-2' customY='-top-2' />
          ) : (
            ''
          )}
        </div>

        <button
          className={`button-secondary h-10 w-full bg-white ${forFooter ? 'md:w-fit' : 'sm:w-fit'}`}
          type='submit'
        >
          {t('footer.subscribe')}
        </button>
      </form>
      <span
        className={`text-100 w-full text-pretty text-center ${forFooter ? 'md:max-w-[440px] md:text-left' : ''}`}
      >
        {t('footer.never_spam')}
      </span>
    </>
  );
};
